import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLang, useTheme } from "@/contexts/AppContexts";
import { Moon, Sun, Languages, ScanSearch, History as HistoryIcon, Sparkles } from "lucide-react";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const isAnalyze = location.pathname === "/";
  const isHistory = location.pathname.startsWith("/history");

  return (
    <header
      className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      data-testid="app-header"
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm transition-shadow group-hover:shadow-md"
            aria-hidden="true"
          >
            <ScanSearch className="h-5 w-5" strokeWidth={2.25} />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-base font-semibold tracking-tight">
              {t("appName")}
            </span>
            <span className="hidden md:inline text-[11px] text-muted-foreground font-mono-plex">
              metadata · ELA · Gemini 2.5 Pro
            </span>
          </div>
        </Link>

        <nav className="flex items-center gap-1">
          <Link
            to="/"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              isAnalyze ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
            }`}
            data-testid="nav-analyze-link"
          >
            <Sparkles className="h-4 w-4" />
            <span>{t("nav.analyze")}</span>
          </Link>
          <Link
            to="/history"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              isHistory ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
            }`}
            data-testid="nav-history-link"
          >
            <HistoryIcon className="h-4 w-4" />
            <span>{t("nav.history")}</span>
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="hidden sm:inline-flex font-mono-plex text-xs"
            onClick={() => setLang(lang === "id" ? "en" : "id")}
            data-testid="language-toggle"
            aria-label="Toggle language"
          >
            <Languages className="h-4 w-4 mr-1.5" />
            <span className="uppercase tracking-wider">{lang}</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </header>
  );
};

export { Header };
export default Header;
